/**
 * Renovatietickets-tabelconfiguratie.
 *
 * Spiegelt de helpdesk-tabel (zelfde UniversalTable, kolomgedrag, kleuren) zodat de
 * renovatietickets-lijst visueel niet te onderscheiden is van de helpdesk-lijst.
 * Renovatie-specifieke velden (renocode, onderdeel-label, type boodschap, contactpersoon)
 * behouden hun eigen inhoud maar gebruiken exact dezelfde styling.
 *
 * @module RenoHelpdesk/TableConfig
 */

import {
  TableColumnsConfig,
  PickerConfig,
  TableSortConfig,
} from 'shared/components/UniversalTable/types';
import { API_URL } from 'constants/index';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { RenoMessageType, RenoTicketStatus } from '../types/renoTicket';

/**
 * Opties voor de keuzelijsten "Type boodschap" en "Fase" in de tabel.
 *
 * Beide gaan via dezelfde picker-modal als de andere kolommen (`custom-tags`).
 * Een dropdown ín de cel werkte niet: elke tabelcel staat op `overflow-hidden`,
 * waardoor het absoluut gepositioneerde menu volledig weggeknipt werd — je klikte
 * dus wel, maar zag niets. Via de modal werkt het identiek aan de helpdesk.
 */
const MESSAGE_TYPE_PICKER_OPTIONS = Object.values(RenoMessageType).map((value) => ({
  value,
  label: value,
}));

/** Fases zoals in de kanban; "Unknown" is geen keuze die een gebruiker maakt. */
const PHASE_PICKER_OPTIONS = Object.values(RenoTicketStatus)
  .filter((status) => status !== RenoTicketStatus.UNKNOWN)
  .map((value) => ({ value, label: value }));

// PATCH-gebaseerde update (reno-backend kent enkel PATCH, niet de PUT die UniversalTable
// standaard gebruikt). Na succes wordt onUpdate aangeroepen zodat de lijst kan resyncen.
const patchField =
  (buildPayload: (val: any) => Record<string, unknown>) =>
  async (
    val: any,
    ticketIds: number[],
    session: any,
    onUpdate: (id: number, payload: any) => void,
  ) => {
    const payload = buildPayload(val);
    for (const id of ticketIds) {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
        session,
      );
      if (!res.ok) throw new Error(`Kon ticket ${id} niet bijwerken`);
      onUpdate(id, payload);
    }
  };

export const RENO_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    { id: 'checkbox', label: '', minWidth: 40, defaultWeight: 0, className: 'flex justify-center' },
    { id: 'actions', label: 'Acties', minWidth: 100, defaultWeight: 0, className: 'flex justify-center' },
    { id: 'comm', label: 'Comm', minWidth: 80, defaultWeight: 0, className: 'flex justify-center' },
    { id: 'priority', label: 'Prio', minWidth: 60, defaultWeight: 0, sortKey: 'priority', className: 'flex justify-center' },
    // Titel + onderdeel-label + projectbadge zitten in één cel; op 150px bleef er van
    // de titel een paar woorden over en van de labelchip een letter. De ondergrens blijft
    // gematigd — het gewicht doet het werk, een te hoge `minWidth` duwt de tabel op een
    // smal scherm in de horizontale scroll.
    { id: 'ticket-label', label: 'Ticket & Label', minWidth: 220, defaultWeight: 14, sortKey: 'title' },
    { id: 'building', label: 'Gebouw', minWidth: 80, defaultWeight: 3, sortKey: 'building', visible: 'lg' },
    { id: 'unit', label: 'Unit', minWidth: 70, defaultWeight: 2, sortKey: 'unit', visible: 'lg' },
    { id: 'message-type', label: 'Type boodschap', minWidth: 120, defaultWeight: 2, visible: 'xl' },
    { id: 'tags', label: 'Categorie', minWidth: 110, defaultWeight: 2, visible: 'lg' },
    { id: 'reporter', label: 'Contactpersoon', minWidth: 100, defaultWeight: 2.5, sortKey: 'reporter', visible: 'lg' },
    { id: 'supplier', label: 'Leverancier', minWidth: 100, defaultWeight: 2.5, sortKey: 'supplier', visible: 'lg' },
    { id: 'assignee', label: 'Toegewezen', minWidth: 80, defaultWeight: 1.5, sortKey: 'assignee' },
    // Smaller ten voordele van "Ticket & Label": de vier fasenamen zijn kort
    // ("Info ontvangen" is de langste) en de pill kapt af met "…" + tooltip.
    { id: 'internal-status', label: 'Fase', minWidth: 96, defaultWeight: 1, sortKey: 'status', visible: 'lg' },
    { id: 'intervention-date', label: 'Interventie', minWidth: 90, defaultWeight: 0, sortKey: 'interventionDate', visible: 'lg' },
    { id: 'quote-amount', label: 'Offerte', minWidth: 90, defaultWeight: 0, sortKey: 'approvedQuoteAmount', visible: 'xl' },
    { id: 'invoice-amount', label: 'Factuur', minWidth: 90, defaultWeight: 0, sortKey: 'invoiceAmount', visible: 'xl' },
    { id: 'days-in-phase', label: 'Dagen in Fase', minWidth: 80, defaultWeight: 0, sortKey: 'daysInCurrentPhase', visible: 'lg' },
    { id: 'date', label: 'Datum', minWidth: 70, defaultWeight: 0, sortKey: 'date', visible: 'lg' },
  ],
  // v2: "Ticket & Label" breder ten koste van "Fase". De breedtes worden bij élke mount
  // weggeschreven, dus zonder nieuwe sleutel blijft wie de lijst al opende de oude zien.
  localStorageKey: 'renoListColumnWidthsV2',
};

/**
 * Kolommen voor de "per project"-weergave: dezelfde kolommen als de vlakke
 * lijst, maar met "Project" en "Onderdeel" als eerste inhoudelijke kolommen.
 * De titelkolom laat daar zijn project-/onderdeel-badges vallen (zie
 * `createRenoCells({ options: { withProjectColumns: true } })`).
 *
 * Eigen localStorage-key, zodat kolombreedtes per weergave bewaard blijven.
 */
export const RENO_PROJECT_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    { id: 'checkbox', label: '', minWidth: 40, defaultWeight: 0, className: 'flex justify-center' },
    { id: 'actions', label: 'Acties', minWidth: 100, defaultWeight: 0, className: 'flex justify-center' },
    { id: 'comm', label: 'Comm', minWidth: 80, defaultWeight: 0, className: 'flex justify-center' },
    { id: 'priority', label: 'Prio', minWidth: 60, defaultWeight: 0, sortKey: 'priority', className: 'flex justify-center' },
    { id: 'project', label: 'Project', minWidth: 130, defaultWeight: 4 },
    { id: 'project-part', label: 'Onderdeel', minWidth: 120, defaultWeight: 3 },
    // Breder dan voorheen (150/7); project en onderdeel staan hier in eigen kolommen,
    // dus de titel heeft de cel voor zich alleen en mag ze ook volledig gebruiken.
    { id: 'ticket-label', label: 'Ticket', minWidth: 200, defaultWeight: 10, sortKey: 'title' },
    { id: 'building', label: 'Gebouw', minWidth: 80, defaultWeight: 2, sortKey: 'building', visible: 'lg' },
    { id: 'unit', label: 'Unit', minWidth: 70, defaultWeight: 2, sortKey: 'unit', visible: 'lg' },
    { id: 'message-type', label: 'Type boodschap', minWidth: 120, defaultWeight: 2, visible: 'xl' },
    { id: 'tags', label: 'Categorie', minWidth: 110, defaultWeight: 2, visible: 'lg' },
    { id: 'reporter', label: 'Contactpersoon', minWidth: 100, defaultWeight: 2, sortKey: 'reporter', visible: 'lg' },
    { id: 'supplier', label: 'Leverancier', minWidth: 100, defaultWeight: 2, sortKey: 'supplier', visible: 'lg' },
    { id: 'assignee', label: 'Toegewezen', minWidth: 80, defaultWeight: 1.5, sortKey: 'assignee' },
    { id: 'internal-status', label: 'Fase', minWidth: 96, defaultWeight: 1, sortKey: 'status', visible: 'lg' },
    { id: 'intervention-date', label: 'Interventie', minWidth: 90, defaultWeight: 0, sortKey: 'interventionDate', visible: 'lg' },
    { id: 'quote-amount', label: 'Offerte', minWidth: 90, defaultWeight: 0, sortKey: 'approvedQuoteAmount', visible: 'xl' },
    { id: 'invoice-amount', label: 'Factuur', minWidth: 90, defaultWeight: 0, sortKey: 'invoiceAmount', visible: 'xl' },
    { id: 'days-in-phase', label: 'Dagen in Fase', minWidth: 80, defaultWeight: 0, sortKey: 'daysInCurrentPhase', visible: 'lg' },
    { id: 'date', label: 'Datum', minWidth: 70, defaultWeight: 0, sortKey: 'date', visible: 'lg' },
  ],
  // v2: zelfde herverdeling als de vlakke lijst — zie de opmerking bij
  // RENO_COLUMNS_CONFIG.localStorageKey.
  localStorageKey: 'renoProjectListColumnWidthsV2',
};

/**
 * Picker-configuratie van de renovatietickets-tabel.
 *
 * `tagCatalog` vult de keuzelijst van de Categorie-kolom. Dat is dezelfde lijst als
 * de categorieën van een projectonderdeel (`GET /reno-helpdesk/tags` leest
 * `renovation_label`) en is dus pas bekend op runtime — vandaar een functie in plaats
 * van een constante. Zonder catalogus blijft de kolom zichtbaar, maar valt er niets
 * te kiezen.
 */
export const createRenoPickerConfig = (tagCatalog: string[] = []): PickerConfig => ({
  entityName: 'ticket',
  entityPrefix: 'RS-',
  apiUpdateEndpoint: '/reno-helpdesk/tickets/{id}',
  entityPlural: 'tickets',

  fieldMappings: [
    {
      eventMode: 'project',
      pickerMode: 'renovation-project',
      fieldName: 'renovationProjectId',
      titleTemplate: 'Selecteer renovatieproject voor {count} {entityName}',
      extractCurrentValue: (row) => row.renovationProject ?? null,
      // Het gebouw van het ticket: daarmee toont de picker meteen de projecten van
      // dat gebouw i.p.v. te wachten op twee getypte letters.
      extractBuildingId: (row) => row.buildingId ?? row.building?.id,
      customUpdateHandler: patchField((val) => ({ renovationProjectId: val?.id ?? null, projectPartId: null })),
    },
    {
      eventMode: 'part',
      pickerMode: 'project-part',
      fieldName: 'projectPartId',
      titleTemplate: 'Selecteer onderdeel voor {count} {entityName}',
      extractCurrentValue: (row) => row.projectPart ?? null,
      // De onderdeel-picker filtert op renovatieproject; zonder expliciete sleutel
      // belandde deze waarde in `currentBuildingId` en bleef de picker
      // "Selecteer eerst een renovatieproject" tonen.
      extractContextValue: (row) => row.renovationProjectId ?? row.renovationProject?.id ?? null,
      contextConfigKey: 'currentRenovationProjectId',
      // Heeft het ticket nog geen project, dan kiest de picker er eerst één (zie
      // UniversalPickerModal). Dit gebouw beperkt die eerste stap tot de projecten
      // van het ticket zijn gebouw.
      extractBuildingId: (row) => row.buildingId ?? row.building?.id,
      // `renovationProjectId` staat enkel op de waarde wanneer de picker in stap 1
      // zelf een project gekozen heeft; anders zou het project van het ticket
      // overschreven worden met undefined.
      customUpdateHandler: patchField((val) => ({
        projectPartId: val?.id ?? null,
        ...(val?.renovationProjectId ? { renovationProjectId: val.renovationProjectId } : {}),
      })),
    },
    {
      eventMode: 'building',
      pickerMode: 'building',
      fieldName: 'buildingId',
      titleTemplate: 'Selecteer gebouw voor {count} {entityName}',
      extractCurrentValue: (row) => row.building ?? (row.buildingId ? { id: row.buildingId } : null),
      customUpdateHandler: patchField((val) => ({ buildingId: val?.id ?? null })),
    },
    {
      eventMode: 'unit',
      pickerMode: 'unit',
      fieldName: 'unitId',
      titleTemplate: 'Selecteer unit voor {count} {entityName}',
      extractCurrentValue: (row) => row.unit ?? (row.unitId ? { id: row.unitId } : null),
      extractContextValue: (row) => row.buildingId ?? row.building?.id,
      customUpdateHandler: patchField((val) => ({ unitId: val?.id ?? null })),
    },
    {
      eventMode: 'reporter',
      pickerMode: 'reporter',
      fieldName: 'reporterId',
      titleTemplate: 'Selecteer contactpersoon voor {count} {entityName}',
      extractCurrentValue: (row) => row.reporter ?? null,
      customUpdateHandler: patchField((val) => ({ reporterId: val?.id ?? null })),
    },
    {
      // Meerdere leveranciers: het kiezen van een leverancier VOEGT TOE (koppeltabel).
      eventMode: 'supplier',
      pickerMode: 'supplier',
      fieldName: 'supplierId',
      titleTemplate: 'Leverancier toevoegen aan {count} {entityName}',
      extractCurrentValue: () => null,
      customUpdateHandler: async (val, ticketIds, session, onUpdate) => {
        if (!val?.id) return;
        for (const id of ticketIds) {
          const res = await fetchWithAuth(
            `${API_URL}/reno-helpdesk/tickets/${id}/suppliers`,
            { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ supplierId: val.id }) },
            session,
          );
          if (!res.ok) throw new Error(`Kon leverancier niet toevoegen aan ticket ${id}`);
          onUpdate(id, {});
        }
      },
    },
    {
      eventMode: 'assignee',
      pickerMode: 'assignee',
      fieldName: 'assignedTo',
      titleTemplate: 'Selecteer verantwoordelijke voor {count} {entityName}',
      extractCurrentValue: (row) => (row.assignedTo ? { id: row.assignedTo } : null),
      customUpdateHandler: patchField((val) => ({ assignedTo: val?.id ?? null })),
    },
    {
      eventMode: 'messageType',
      pickerMode: 'custom-tags' as any,
      fieldName: 'messageType',
      titleTemplate: 'Selecteer type boodschap voor {count} {entityName}',
      extractCurrentValue: (row) => row.messageType ?? null,
      customOptions: MESSAGE_TYPE_PICKER_OPTIONS,
      customUpdateHandler: patchField((val) => ({ messageType: val ?? null })),
    },
    {
      eventMode: 'phase',
      pickerMode: 'custom-tags' as any,
      fieldName: 'status',
      titleTemplate: 'Selecteer fase voor {count} {entityName}',
      extractCurrentValue: (row) => row.status ?? null,
      customOptions: PHASE_PICKER_OPTIONS,
      customUpdateHandler: patchField((val) => ({ status: val })),
    },
    {
      // Interventiedatum: handmatig veld, zoals "Interventie gepland" bij de helpdesk.
      eventMode: 'interventionDate',
      pickerMode: 'document-date' as any,
      fieldName: 'interventionDate',
      titleTemplate: 'Interventiedatum voor {count} {entityName}',
      extractCurrentValue: (row) => row.interventionDate ?? null,
      customUpdateHandler: patchField((val) => ({ interventionDate: val || null })),
    },
    {
      eventMode: 'tags',
      pickerMode: 'custom-tags' as any,
      fieldName: 'tags',
      titleTemplate: 'Selecteer categorie voor {count} {entityName}',
      extractCurrentValue: (row) => row.tags ?? [],
      customOptions: tagCatalog.map((tag) => ({ value: tag, label: tag })),
      multiSelect: true,
      customUpdateHandler: patchField((val) => ({ tags: Array.isArray(val) ? val : val ? [val] : [] })),
    },
  ],

  fieldLabels: {
    renovationProjectId: 'Project',
    projectPartId: 'Onderdeel',
    buildingId: 'Gebouw',
    unitId: 'Unit',
    reporterId: 'Contactpersoon',
    supplierId: 'Leverancier',
    assignedTo: 'Toegewezen aan',
    messageType: 'Type boodschap',
    status: 'Fase',
    tags: 'Categorie',
    interventionDate: 'Interventiedatum',
  },
});

/** Standaardconfiguratie (zonder tagcatalogus); pagina's die tags willen laten
 *  kiezen gebruiken {@link createRenoPickerConfig} met hun eigen catalogus. */
export const RENO_PICKER_CONFIG: PickerConfig = createRenoPickerConfig();

export const RENO_SORT_CONFIG: TableSortConfig = {
  defaultSortKey: 'date',
  defaultDirection: 'desc',
  fields: [
    {
      key: 'priority',
      extractor: (row) => ({ Hoog: 3, Gemiddeld: 2, Laag: 1, Unknown: 0 } as Record<string, number>)[row.priority] || 0,
    },
    { key: 'building', extractor: (row) => row.buildingId || row.building?.id || '' },
    { key: 'unit', extractor: (row) => row.unitId || row.unit?.id || '' },
    { key: 'date', extractor: (row) => new Date(row.createdAt || '').getTime() },
    { key: 'supplier', extractor: (row) => row.supplierAssignments?.[0]?.supplier?.name || row.supplier?.name || '' },
    { key: 'title', extractor: (row) => (row.title || '').toLowerCase() },
    {
      key: 'reporter',
      extractor: (row) =>
        row.reporter ? `${row.reporter.firstName || ''} ${row.reporter.lastName || ''}`.trim().toLowerCase() : '',
    },
    { key: 'assignee', extractor: (row) => row.assignedTo || '' },
    {
      key: 'status',
      extractor: (row) =>
        ({ 'Geen reactie': 4, Lopende: 3, 'Info ontvangen': 2, Afgerond: 1, Unknown: 0 } as Record<string, number>)[
          row.status
        ] || 0,
    },
    { key: 'daysInCurrentPhase', extractor: (row) => row.daysInCurrentPhase || 0 },
    // Tickets zonder interventiedatum groeperen aan één kant i.p.v. tussen de rest.
    {
      key: 'interventionDate',
      extractor: (row) => (row.interventionDate ? new Date(row.interventionDate).getTime() : 0),
    },
    { key: 'approvedQuoteAmount', extractor: (row) => row.approvedQuoteAmount || 0 },
    { key: 'invoiceAmount', extractor: (row) => row.invoiceAmount || 0 },
  ],
  caseSensitive: false,
};

export const RENO_TABLE_CONFIG = {
  columnsConfig: RENO_COLUMNS_CONFIG,
  pickerConfig: RENO_PICKER_CONFIG,
  sortConfig: RENO_SORT_CONFIG,
};

/**
 * Sortering van de "per project"-weergave. Standaard sorteert de tabel op de
 * volgorde die de backend meegaf (project → onderdeel → ticketdatum); zonder
 * die sleutel zou de tabel op ticketdatum sorteren en zouden de projectgroepen
 * op hun nieuwste ticket komen te staan i.p.v. op projectnaam.
 * De gewone kolomsorteringen blijven beschikbaar.
 */
export const RENO_PROJECT_SORT_CONFIG: TableSortConfig = {
  ...RENO_SORT_CONFIG,
  defaultSortKey: 'groupOrder',
  defaultDirection: 'asc',
  fields: [
    ...RENO_SORT_CONFIG.fields,
    { key: 'groupOrder', extractor: (row) => row.groupOrder ?? 0 },
  ],
};

/** Tabelconfiguratie van de "per project"-weergave (zelfde pickers als de vlakke lijst). */
export const RENO_PROJECT_TABLE_CONFIG = {
  columnsConfig: RENO_PROJECT_COLUMNS_CONFIG,
  pickerConfig: RENO_PICKER_CONFIG,
  sortConfig: RENO_PROJECT_SORT_CONFIG,
};
